/**
 * ElementarySynthNode - Audio synthesis using Elementary Audio WASM engine
 *
 * A high-quality synthesizer using Elementary Audio's WASM-based DSP.
 * Features band-limited oscillators (blepsaw, blepsquare) and unison with detune.
 *
 * Endpoints:
 * - event (input): note events to play
 * - audioOut (output): audio output for routing
 * - oscillator (input): waveform type
 * - unisonVoices, unisonSpread (input): unison settings
 * - attack, decay, sustain, release (input): envelope params
 * - detune (input): global detune in cents
 * - volume (input): output volume in dB
 */

import { ElementarySynthBase, VoiceSlot } from '@elementary/nodes/ElementarySynthBase';
import { Domain } from '@nodes/Endpoint';
import type { AudioDependencies } from '@audio/nodes/AudioBaseNode';
import type { Payload } from '@graph/Payload';
import type { NodeDef } from '@runtime/IRuntime';
import { envelopeEndpoints, synthDefaultEndpoints } from '@audio/sharedEndpoints';

import { ELEMENTARY_SYNTH_PRESETS } from './presets';

// Elementary imports
import { el, NodeRepr_t } from '@elemaudio/core';
import { audio, events, params } from '@/nodes/TypedEndpoints';

// ─────────────────────────────────────────────────────────────
// Types
// ─────────────────────────────────────────────────────────────

export type ElemOscillatorType = 'sine' | 'triangle' | 'square' | 'sawtooth' | 'blepsaw' | 'blepsquare';

// ─────────────────────────────────────────────────────────────
// Endpoints
// ─────────────────────────────────────────────────────────────

const endpoints = {
  ...audio({
    audioOut: { isOutput: true },
  }),
  ...events({
    event: { dataType: 'number', dataFlow: 'event', isInput: true, primary: true },
  }),
  ...params({
    preset: { dataType: 'string', options: ELEMENTARY_SYNTH_PRESETS.map(p => p.name) },
    volume: { order: 1, value: -12, min: -60, max: 0, step: 1 },
    ...synthDefaultEndpoints,
    oscillator: { group: 'Oscillator', dataType: 'string', value: 'blepsaw', options: ['sine', 'triangle', 'square', 'sawtooth', 'blepsaw', 'blepsquare'] as const },
    unisonVoices: { group: 'Oscillator', value: 3, min: 1, max: 8, step: 1 },
    unisonSpread: { group: 'Oscillator', value: 20, min: 0, max: 100, step: 1 },
    detune: { order: 3, value: 0, min: -5, max: 5, step: 0.01 },
    octave: { order: 2, value: 0, min: -4, max: 4, step: 1 },
    ...envelopeEndpoints
  })

};

// ─────────────────────────────────────────────────────────────
// Node
// ─────────────────────────────────────────────────────────────

export class ElementarySynthNode extends ElementarySynthBase<typeof endpoints> {
  constructor(id: string, deps: AudioDependencies) {
    super(id, deps);
  }

  // ─────────────────────────────────────────────────────────────
  // Input Handling
  // ─────────────────────────────────────────────────────────────

  onParamChanged(payload: Payload, endpointId: string): void {
    switch (endpointId) {
      case 'envelope':
        this.setFullEnvelope(payload.value);
        break;
      case 'volume':
      case 'oscillator':
      case 'unisonVoices':
      case 'unisonSpread':
      case 'detune':
      case 'octave':
      case 'attack':
      case 'decay':
      case 'sustain':
      case 'release':
        if (this.isInitialized) {
          this.render();
        }
        break;
    }
  }

  // ─────────────────────────────────────────────────────────────
  // DSP / Audio Graph
  // ─────────────────────────────────────────────────────────────

  private buildOscillator(freq: NodeRepr_t, voiceId: string, unisonIndex = 0): NodeRepr_t {
    const type = this.params.oscillator.value;

    switch (type) {
      case 'sine':
        return el.cycle(freq);
      case 'triangle':
        return el.mul(el.const({ key: `${voiceId}_osc_${unisonIndex}_gain`, value: 0.8 }), el.triangle(freq));
      case 'square':
        return el.square(freq);
      case 'sawtooth':
        return el.saw(freq);
      case 'blepsaw':
        return el.blepsaw(freq);
      case 'blepsquare':
        return el.blepsquare(freq);
      default:
        return el.cycle(freq);
    }
  }

  private buildUnisonOscillators(baseFreq: number, voiceId: string): NodeRepr_t {
    const voices = this.params.unisonVoices.value;
    const spread = this.params.unisonSpread.value;

    if (voices === 1) {
      const freq = el.const({ key: `${voiceId}_freq`, value: baseFreq });
      return this.buildOscillator(freq, voiceId, 0);
    }


    const oscillators: NodeRepr_t[] = [];

    for (let i = 0; i < voices; i++) {
      const detuneRatio = voices > 1 ? (i / (voices - 1)) - 0.5 : 0;
      const detuneCents = detuneRatio * spread;
      const detuneMultiplier = Math.pow(2, detuneCents / 1200);

      const freq = el.const({
        key: `${voiceId}_freq_${i}`,
        value: baseFreq * detuneMultiplier
      });

      oscillators.push(this.buildOscillator(freq, voiceId, i));
    }

    let mixed: NodeRepr_t = oscillators[0];
    for (let i = 1; i < oscillators.length; i++) {
      mixed = el.add(mixed, oscillators[i]);
    }

    return el.mul(el.const({ key: `${voiceId}_norm`, value: 1 / voices }), mixed);
  }

  private buildVoice(slot: VoiceSlot, voiceId: string): NodeRepr_t {
    // Apply octave transposition and detune
    const transposedNote = slot.note + (this.params.octave.value * 12);
    const baseFreq = 440 * Math.pow(2, (transposedNote - 69) / 12);
    const detune = this.params.detune.value;
    const detunedFreq = baseFreq * Math.pow(2, detune / 1200);

    const osc = this.buildUnisonOscillators(detunedFreq, voiceId);
    const gateSignal = this.buildTimeGate(voiceId, slot.startSample, slot.endSample);
    const env = this.buildEnvelope(gateSignal);
    const velGain = el.const({ key: `${voiceId}_vel`, value: slot.velocity });

    return el.mul(osc, el.mul(env, velGain));
  }

  render(): void {
    if (!this.core || !this.isInitialized) {
      this.logTrace('render skipped: not initialized');
      return;
    }

    // Always render all voice slots - graph structure stays constant
    const voiceOutputs: NodeRepr_t[] = [];

    for (let i = 0; i < this.voiceSlots.length; i++) {
      const slot = this.voiceSlots[i];
      const voiceId = `voice_${i}`;
      voiceOutputs.push(this.buildVoice(slot, voiceId));
    }

    // Sum all voices
    let output: NodeRepr_t = voiceOutputs[0];
    for (let i = 1; i < voiceOutputs.length; i++) {
      output = el.add(output, voiceOutputs[i]);
    }

    this.applyVolumeAndRender(output);
  }
}

// ─────────────────────────────────────────────────────────────
// Node Definition
// ─────────────────────────────────────────────────────────────

export const elementarySynthNodeDef: NodeDef<ElementarySynthNode> = {
  type: 'Synth',
  shortName: 'synth',
  info: 'Elementary Audio WASM synth with band-limited oscillators and unison detune.',
  domain: Domain.Audio,
  endpoints,
  factoryPresets: ELEMENTARY_SYNTH_PRESETS,
  ctor: ElementarySynthNode
};
